import { Box, Typography } from "@/components/ui";
import { useApp } from "@/context/AppContext";
import logoMini from "@/assets/logo-mini.png";

const spinKeyframes = {
  "@keyframes appLoadingSpin": {
    to: { transform: "rotate(360deg)" },
  },
};

// Logo/brand colors – same as theme primary palette
const ringGradient =
  "conic-gradient(from 0deg, #1561ED, #4A83F1, #1252CC, #0D47A1, #1561ED)";
const textGradient =
  "linear-gradient(135deg, #1561ED 0%, #4A83F1 50%, #1252CC 100%)";

/**
 * App-wide loading indicator with one colorful spinning ring and center image.
 * Use for full-page load (fullPage) or inline/overlay (default).
 * @param {string} [centerImage] - Image URL for the center; defaults to logo-mini.png from assets.
 */
const AppLoading = ({
  fullPage = false,
  size = "medium",
  message,
  overlay = false,
  centerImage = logoMini,
  sx = {},
}) => {
  const { t } = useApp();
  const text = message ?? t("loading");

  const sizeMap = {
    small: { ring: 100, thickness: 4, center: 95 },
    medium: { ring: 130, thickness: 4, center: 125 },
    large: { ring: 172, thickness: 4, center: 166 },
  };
  const { ring: ringSize, thickness, center: centerSize } =
    sizeMap[size] || sizeMap.medium;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        ...(fullPage && {
          minHeight: 320,
          width: "100%",
        }),
        ...(overlay && {
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: "background.paper",
          zIndex: 10,
          opacity: 0.96,
        }),
        ...sx,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: ringSize,
          height: ringSize,
          ...spinKeyframes,
        }}
      >
        {/* Single ring - colorful, rotates */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            background: ringGradient,
            animation: "appLoadingSpin 1s linear infinite",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: thickness,
            borderRadius: "50%",
            bgcolor: "background.paper",
          }}
        />

        {/* Center: logo image */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: centerSize,
            height: centerSize,
            borderRadius: "50%",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.paper",
            boxShadow: 2,
          }}
        >
          <Box
            component="img"
            src={centerImage}
            alt=""
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          background: textGradient,
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          color: "transparent",
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

export default AppLoading;
